import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';
import { ILocalization, LocalizedText } from 'bernie-l10n';

import { PropertyInfoPolicyViewModel, PropertyInfoSubSectionViewModel } from 'models';

import { LazyImage } from 'components/common/lazy/lazy-image';
import { NavigationRefs } from 'components/infosite/navigation/navigation-refs.tsx';
import { PropertyDetailsSegment } from './property-details-segment';
import { PropertyPoliciesSection } from './/property-policies-section';
import { renderAmenityBody } from './render-amenity-helper';

interface PropertyPoliciesProps {
  analytics?: AnalyticsStore;
  isToggleHidden?: boolean;
  l10n: ILocalization;
  policies?: PropertyInfoPolicyViewModel;
  petsPolicy?: PropertyInfoSubSectionViewModel;
}

export const PropertyPolicies: React.FunctionComponent<PropertyPoliciesProps> = ({
  analytics,
  isToggleHidden,
  l10n,
  petsPolicy,
  policies
}) => {
  const info = policies && policies.info;
  const paymentOptions = info && info.paymentOptions;
  const showPaymentOptions = Boolean(paymentOptions && paymentOptions.length > 0);
  const childBeds = info && info.childBeds;
  const checkinInstructions = info && info.checkinInstructions;
  const checkinPolicies = info && info.checkinPolicies;
  const checkoutPolicies = info && info.checkoutPolicies;
  const accessMethodPolicies = info && info.accessMethodPolicies;
  const showPetsPolicy = Boolean(petsPolicy && petsPolicy.title && petsPolicy.body && petsPolicy.body.length > 0);
  const showHouseRules = info && info.houseRules && info.houseRules.title && info.houseRules.content && info.houseRules.content.length > 0;

  const secondaryTitleClassNames = 'policies__header--secondary uitk-type-heading-500 uitk-type-bold';
  const secondaryTitleSmClassNames = 'policies__header--secondary uitk-type-300 uitk-type-bold pwa-theme--grey-800';
  const noBulletClasses = 'all-t-margin-two uitk-type-300 pwa-theme--grey-700';
  const policiesAnalytics = analytics.setupTracker('.Policies', 'SeeMore_SeeLess');

  return (
    Boolean(policies) && (
      <PropertyDetailsSegment
        analytics={policiesAnalytics}
        dataStid="section-property-policies"
        errorBoundaryLabel="hotel.information.policies.propertyPolicies"
        isToggleHidden={isToggleHidden}
        rootClassName="property-policies"
        title={policies.title}
        navigationRef={NavigationRefs.Policies}
      >
        {showHouseRules && (
          <div data-stid="property-policies-house-rules" className="all-y-padding-three">
            <h4 className={secondaryTitleClassNames}>{info.houseRules.title}</h4>
            {info.houseRules.content.map(
              (rule, index) =>
                rule &&
                rule.length > 0 && (
                  <div key={index} className={noBulletClasses}>
                    {rule}
                  </div>
                )
            )}
          </div>
        )}
        <PropertyPoliciesSection
          id="property-policies-check-in"
          model={checkinPolicies}
          titleLocToken="hotels.infosite.policies.checkIn.title"
          secondaryTitleClassNames={secondaryTitleClassNames}
        />
        <PropertyPoliciesSection
          id="check-in-instructions"
          model={checkinInstructions}
          titleLocToken="hotels.infosite.policies.specialCheckInInstructions.title"
          secondaryTitleClassNames={secondaryTitleSmClassNames}
        />
        <PropertyPoliciesSection
          id="property-policies-check-out"
          model={checkoutPolicies}
          titleLocToken="hotels.infosite.policies.checkOut.title"
          secondaryTitleClassNames={secondaryTitleClassNames}
        />
        <PropertyPoliciesSection
          id="access-methods"
          model={accessMethodPolicies}
          titleLocToken="hotels.infosite.policies.accessMethods.title"
          secondaryTitleClassNames={secondaryTitleClassNames}
        />
        <PropertyPoliciesSection
          id="child-beds"
          model={childBeds}
          titleLocToken="hotels.infosite.policies.childrenExtraBeds.title"
          secondaryTitleClassNames={secondaryTitleClassNames}
        />
        {showPetsPolicy && (
          <div className="pets-policy all-y-padding-three">
            <h4 className={`pets-policy__title ${secondaryTitleClassNames}`}>{petsPolicy.title}</h4>
            {petsPolicy.body.map((text, index) => renderAmenityBody(text, index, 'pets-policy__details'))}
          </div>
        )}
        {showPaymentOptions && (
          <div className="payment-types all-y-padding-three">
            <h4 className={`payment-types__title ${secondaryTitleClassNames}`}>
              <LocalizedText message="hotels.infosite.policies.paymentTypes.title" />
            </h4>
            <div className="payment-types__cards">
              {paymentOptions.map((type, index) => (
                <LazyImage
                  experimentName="PWA_LAZY_LOAD_IMAGES"
                  altPlaceholderText={type.name ? type.name : l10n.formatText('hotels.infosite.policies.paymentType.altText')}
                >
                  <img src={type.imageUrl} alt={type.name ? type.name : l10n.formatText('hotels.infosite.policies.paymentType.altText')} />
                </LazyImage>
              ))}
            </div>
          </div>
        )}
      </PropertyDetailsSegment>
    )
  );
};
